import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { 
  getAllSources, 
  getSource, 
  createSource, 
  updateSource, 
  deleteSource,
  processSource,
  getSourceUrls,
  addSourceUrls
} from '../sourceService';
import { 
  SourceItem, 
  ICreateSourceRequest, 
  IUpdateSourceRequest
} from '../../../../models';

// Query keys
export const sourceKeys = {
  all: ['sources'] as const,
  lists: () => [...sourceKeys.all, 'list'] as const,
  list: () => [...sourceKeys.lists()] as const,
  details: () => [...sourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...sourceKeys.details(), id] as const,
  urls: (id: string) => [...sourceKeys.detail(id), 'urls'] as const,
  urlsByStatus: (id: string, status?: string) => [...sourceKeys.urls(id), { status }] as const,
};

// Get all sources
export const useSources = () => {
  return useQuery({
    queryKey: sourceKeys.list(),
    queryFn: () => getAllSources(),
  });
};

// Get a source by ID
export const useSource = (id: string) => {
  return useQuery({
    queryKey: sourceKeys.detail(id),
    queryFn: () => getSource(id),
    enabled: !!id,
  });
};

// Create a new source
export const useCreateSource = () => {
  return useMutation({
    mutationFn: (data: ICreateSourceRequest) => createSource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.lists() });
    },
  });
};

// Update an existing source
export const useUpdateSource = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUpdateSourceRequest }) => 
      updateSource(id, data),
    onSuccess: (updatedSource: SourceItem) => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.detail(updatedSource.id) });
      queryClient.invalidateQueries({ queryKey: sourceKeys.lists() });
    },
  });
};

// Delete a source
export const useDeleteSource = () => {
  return useMutation({
    mutationFn: (id: string) => deleteSource(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.lists() });
      queryClient.removeQueries({ queryKey: sourceKeys.detail(id) });
    },
  });
};

// Process a source
export const useProcessSource = () => {
  return useMutation({
    mutationFn: (id: string) => processSource(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.detail(id) });
    },
  });
};

// Get URLs for a source
export const useSourceUrls = (id: string, status?: string, active?: boolean) => {
  return useQuery({
    queryKey: [...sourceKeys.urlsByStatus(id, status), active],
    queryFn: () => getSourceUrls(id, status, active),
    enabled: !!id,
  });
};

// Add URLs to a source
export const useAddSourceUrls = () => {
  return useMutation({
    mutationFn: ({ id, urls }: { id: string; urls: string[] }) => 
      addSourceUrls(id, urls),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.urls(id) });
    },
  });
};
